import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, formatApiError } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Send, X, Eye, EyeOff, Sparkles, Save, Loader2, Plus, Trash2, Tag, LayoutTemplate, Reply, Zap,
} from "lucide-react";
import DevicePreviewIframe from "../../components/email/DevicePreviewIframe";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 2, 3] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "link"],
    [{ color: [] }, { background: [] }],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "blockquote", "link", "color", "background",
];

/**
 * Convierte el HTML del editor en texto plano (versión "alt" del email).
 */
function htmlToText(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * RecipientChips — input con chips para emails (To/CC/BCC).
 */
function RecipientChips({ label, value, onChange, testId, placeholder }) {
  const [input, setInput] = useState("");
  const addEmail = (raw) => {
    const email = raw.trim().replace(/[,;]/g, "");
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(`Email inválido: ${email}`);
      return;
    }
    if (value.includes(email)) return;
    onChange([...value, email]);
    setInput("");
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-gray-600">{label}</Label>
      <div className="flex flex-wrap gap-1 items-center border border-gray-300 rounded-sm px-2 py-1.5 min-h-[42px] bg-white focus-within:border-brand-burgundy">
        {value.map((email, idx) => (
          <span key={idx} className="inline-flex items-center gap-1 bg-brand-cream text-brand-burgundy text-xs px-2 py-1 rounded-sm border border-brand-burgundy/20" data-testid={`${testId}-chip-${idx}`}>
            {email}
            <button type="button" onClick={() => remove(idx)} className="hover:text-red-700">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (["Enter", ",", ";", "Tab"].includes(e.key)) {
              if (input.trim()) {
                e.preventDefault();
                addEmail(input);
              }
            } else if (e.key === "Backspace" && !input && value.length > 0) {
              remove(value.length - 1);
            }
          }}
          onBlur={() => input.trim() && addEmail(input)}
          placeholder={placeholder || "email@dominio.com (enter o coma para agregar)"}
          className="flex-1 min-w-[180px] outline-none text-sm py-1"
          data-testid={`${testId}-input`}
        />
      </div>
    </div>
  );
}

/**
 * AIComposeModal — modal para que el admin describa su intención y la IA redacta.
 */
function AIComposeModal({ open, onClose, onGenerated, replyToSubject }) {
  const [intent, setIntent] = useState("");
  const [tone, setTone] = useState("profesional");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (intent.trim().length < 5) {
      toast.error("Describe la intención con al menos 5 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/emails/ai-compose", {
        intent: intent.trim(),
        tone,
        context: context.trim() || null,
        reply_to_subject: replyToSubject || null,
      });
      if (data.ok) {
        onGenerated({
          subject: data.subject_suggestion,
          html: data.html,
          text: data.text,
        });
        toast.success("Email generado por IA");
        setIntent(""); setContext("");
        onClose();
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error generando con IA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-burgundy" />
            Redactar con IA
          </DialogTitle>
          <DialogDescription>
            Describe la <strong>intención</strong> del email y la IA lo redactará por ti.
            Puedes ajustar el resultado después.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="ai-intent">¿Qué quieres comunicar? *</Label>
            <Textarea
              id="ai-intent"
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="Ej: Pedirle al cliente que confirme el pago del Diagnóstico antes del viernes y agradecer su paciencia"
              rows={4}
              data-testid="ai-intent-input"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="ai-tone">Tono</Label>
            <select
              id="ai-tone"
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="h-10 w-full border border-gray-300 rounded-sm px-3 text-sm bg-white"
              data-testid="ai-tone-select"
            >
              <option value="profesional">Profesional</option>
              <option value="formal">Formal</option>
              <option value="cercano">Cercano y amable</option>
              <option value="urgente">Urgente</option>
              <option value="conciso">Conciso y directo</option>
              <option value="empático">Empático</option>
            </select>
          </div>
          <div>
            <Label htmlFor="ai-context">Contexto adicional (opcional)</Label>
            <Textarea
              id="ai-context"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Ej: El caso es de Alcaldía de Tunja, contrato de papelería COP 50M, modalidad mínima cuantía."
              rows={3}
              data-testid="ai-context-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button
            onClick={generate}
            disabled={loading || intent.trim().length < 5}
            className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
            data-testid="ai-generate-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generar</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * ComposerForm — formulario completo de composer.
 *
 * Props:
 *  - replyTo: { from_email, to_emails, subject, provider_id, inbound_id, body_quote } → pre-fills
 *  - draftId: string → carga el draft al montar
 *  - onSent: callback (logId) → cuando se envía exitosamente
 *  - onClose: callback → cerrar el composer (modal mode)
 *  - emailsEnabled: bool → muestra warning si kill switch off
 *  - modalMode: bool → si true, oculta el header (lo pone el padre)
 */
export default function ComposerForm({
  replyTo = null,
  draftId: initialDraftId = null,
  onSent,
  onClose,
  emailsEnabled = true,
  modalMode = false,
  splitPreview = false,
}) {
  const [draftId, setDraftId] = useState(initialDraftId);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [replyExpanded, setReplyExpanded] = useState(false);
  const [defaultParts, setDefaultParts] = useState({ header: "", footer: "" });
  const [livePreview, setLivePreview] = useState(splitPreview);
  // Iter18 — Quick reply templates (botones sobre el editor)
  const [quickReplies, setQuickReplies] = useState([]);

  // Iter18 — Cargar quick-reply templates configurados por admin
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/emails/templates/quick-replies");
        if (alive) setQuickReplies(data.items || []);
      } catch {
        // silencioso
      }
    })();
    return () => { alive = false; };
  }, []);

  // Iter18 — aplicar template (usado por Quick Replies y Template Picker)
  const applyTemplate = useCallback((tpl) => {
    if (!tpl) return;
    setSubject(tpl.subject || "");
    setHtml(tpl.html || "");
    api.post(`/admin/emails/templates/${tpl.id}/mark-used`).catch(() => {});
    toast.success(`Plantilla "${tpl.name}" cargada`);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/emails/parts?is_active=true");
        if (!alive) return;
        const items = data.items || [];
        const h = items.find((p) => p.kind === "header" && p.is_default);
        const f = items.find((p) => p.kind === "footer" && p.is_default);
        setDefaultParts({ header: h?.html || "", footer: f?.html || "" });
      } catch {
        // silencioso
      }
    })();
    return () => { alive = false; };
  }, []);
  const [aiOpen, setAiOpen] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);

  // Refs para insertar variables en posición del cursor
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const lastFocusedField = useRef("body"); // "body" | "subject"

  const inReplyTo = replyTo?.provider_id || null;
  const replyInboundId = replyTo?.inbound_id || null;

  // Cargar placeholders
  useEffect(() => {
    api.get("/admin/emails/placeholders")
      .then(({ data }) => setPlaceholders(data.items || []))
      .catch(() => {});
  }, []);

  // Cargar draft si viene
  useEffect(() => {
    if (initialDraftId) {
      api.get(`/admin/emails/drafts/${initialDraftId}`)
        .then(({ data }) => {
          setTo(data.to_emails || []);
          setCc(data.cc_emails || []);
          setBcc(data.bcc_emails || []);
          if ((data.cc_emails || []).length || (data.bcc_emails || []).length) setShowCcBcc(true);
          setSubject(data.subject || "");
          setHtml(data.html || "");
        })
        .catch(() => toast.error("Error cargando borrador"));
    }
  }, [initialDraftId]);

  // Pre-fill desde replyTo
  useEffect(() => {
    if (replyTo && !initialDraftId) {
      if (replyTo.from_email) setTo([replyTo.from_email]);
      if (replyTo.subject) {
        const subj = replyTo.subject;
        setSubject(subj.toLowerCase().startsWith("re:") ? subj : `Re: ${subj}`);
      }
      if (replyTo.body_quote) {
        setHtml(`<p><br></p><blockquote>${replyTo.body_quote}</blockquote>`);
      }
    }
  }, [replyTo, initialDraftId]);

  // Auto-save draft cada 30s si hay cambios
  const lastSavedRef = useRef("");
  useEffect(() => {
    const currentState = JSON.stringify({ to, cc, bcc, subject, html });
    if (currentState === lastSavedRef.current) return;
    if (!subject && !html && to.length === 0) return; // skip vacíos
    const t = setTimeout(async () => {
      try {
        setSavingDraft(true);
        const body = {
          to_emails: to,
          cc_emails: cc,
          bcc_emails: bcc,
          subject,
          html,
          text: htmlToText(html),
          in_reply_to: inReplyTo,
          reply_to_inbound_id: replyInboundId,
        };
        if (draftId) {
          await api.patch(`/admin/emails/drafts/${draftId}`, body);
        } else {
          const { data } = await api.post("/admin/emails/drafts", body);
          setDraftId(data.id);
        }
        lastSavedRef.current = currentState;
        setDraftSavedAt(new Date());
      } catch {
        // silent fail
      } finally {
        setSavingDraft(false);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [to, cc, bcc, subject, html, draftId, inReplyTo, replyInboundId]);

  const saveDraftNow = async () => {
    try {
      setSavingDraft(true);
      const body = {
        to_emails: to, cc_emails: cc, bcc_emails: bcc,
        subject, html, text: htmlToText(html),
        in_reply_to: inReplyTo,
        reply_to_inbound_id: replyInboundId,
      };
      if (draftId) {
        await api.patch(`/admin/emails/drafts/${draftId}`, body);
      } else {
        const { data } = await api.post("/admin/emails/drafts", body);
        setDraftId(data.id);
      }
      setDraftSavedAt(new Date());
      toast.success("Borrador guardado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando borrador");
    } finally {
      setSavingDraft(false);
    }
  };

  /**
   * Inserta una variable en el último campo enfocado (subject o body).
   *  - Subject: inserta en la posición del cursor del input.
   *  - Body: inserta vía Quill API en el rango del cursor.
   * Si no hay focus reciente, inserta al final del body.
   */
  const insertPlaceholder = (key) => {
    if (lastFocusedField.current === "subject" && subjectRef.current) {
      const input = subjectRef.current;
      const start = input.selectionStart ?? subject.length;
      const end = input.selectionEnd ?? subject.length;
      const next = subject.slice(0, start) + key + subject.slice(end);
      setSubject(next);
      // Reposicionar cursor después de la variable
      requestAnimationFrame(() => {
        input.focus();
        const newPos = start + key.length;
        input.setSelectionRange(newPos, newPos);
      });
      return;
    }
    // Default: insertar en el body (Quill editor)
    const editor = quillRef.current?.getEditor?.();
    if (editor) {
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      editor.insertText(range.index, key, "user");
      editor.setSelection(range.index + key.length, 0, "user");
      // Sincronizar estado React con el contenido nuevo
      setHtml(editor.root.innerHTML);
    } else {
      // Fallback simple
      setHtml((html || "") + key);
    }
  };

  const send = async () => {
    if (to.length === 0) {
      toast.error("Debes especificar al menos un destinatario");
      return;
    }
    if (!subject.trim()) {
      toast.error("El asunto es obligatorio");
      return;
    }
    if (!html.trim()) {
      toast.error("El cuerpo no puede estar vacío");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/emails/compose", {
        to_emails: to,
        cc_emails: cc.length ? cc : null,
        bcc_emails: bcc.length ? bcc : null,
        subject: subject.trim(),
        html,
        text: htmlToText(html),
        in_reply_to: inReplyTo,
        draft_id: draftId,
      });
      if (data.sent) {
        toast.success("Email enviado correctamente");
        onSent?.(data.log_id);
        // Reset
        setTo([]); setCc([]); setBcc([]); setSubject(""); setHtml(""); setDraftId(null);
      } else if (data.error === "kill_switch_off") {
        toast.warning("Kill switch activo — el email NO fue enviado.");
      } else {
        toast.error(`No se pudo enviar: ${data.error || "error desconocido"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error enviando");
    } finally {
      setSending(false);
    }
  };

  const previewFull = useMemo(() => {
    // Iter17 — Componer header default + body + footer default (mismo render que el envío real)
    const bodyHtml = html || "<em>Sin contenido</em>";
    const composed = `${defaultParts.header}${bodyHtml}${defaultParts.footer}`;
    return `<!doctype html><html><head><meta charset="utf-8"/><style>
      body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;font-size:14px;line-height:1.55;color:#1f2937;padding:0;margin:0;background:#f3f4f6;}
      a{color:#6F3947;}
      blockquote{border-left:3px solid #d4af37;padding-left:12px;color:#555;margin:8px 0;}
    </style></head><body>${composed}</body></html>`;
  }, [html, defaultParts]);

  // Iter18 — Editor pane reutilizable para split-preview
  const editorBlock = (
    <>
      {!emailsEnabled && (
        <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm text-sm text-amber-900">
          ⚠️ El envío global está <strong>desactivado</strong>. El email se registrará pero no se enviará.
        </div>
      )}

      {replyTo && (
        <div className="border border-brand-burgundy/20 bg-brand-cream-soft rounded-sm overflow-hidden" data-testid="composer-reply-context">
          <div className="px-3 py-2 bg-brand-burgundy/5 border-b border-brand-burgundy/15 flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs">
              <Reply className="w-3.5 h-3.5 text-brand-burgundy" />
              <span className="text-brand-burgundy font-medium">Respondiendo a</span>
              <span className="text-gray-800 font-mono">{replyTo.from_email}</span>
            </div>
            <button
              type="button"
              onClick={() => setReplyExpanded(!replyExpanded)}
              className="text-[10px] uppercase tracking-widest text-gray-600 hover:text-gray-900"
              data-testid="composer-reply-toggle"
            >
              {replyExpanded ? "Ocultar original ↑" : "Ver original ↓"}
            </button>
          </div>
          {replyTo.subject && (
            <div className="px-3 py-1.5 text-xs text-gray-700 border-b border-brand-burgundy/10 truncate">
              <span className="text-gray-500">Asunto orig.:</span> {replyTo.subject}
            </div>
          )}
          {replyExpanded && replyTo.body_quote && (
            <div className="px-3 py-3 bg-white max-h-[280px] overflow-y-auto text-xs text-gray-700 font-mono whitespace-pre-wrap border-t border-gray-200">
              {replyTo.body_quote}
            </div>
          )}
        </div>
      )}

      <RecipientChips label="Para *" value={to} onChange={setTo} testId="composer-to" placeholder="destinatario@dominio.com" />

      {!showCcBcc ? (
        <button
          type="button"
          onClick={() => setShowCcBcc(true)}
          className="text-xs text-brand-burgundy hover:underline"
          data-testid="composer-show-cc-bcc"
        >
          + Agregar CC / BCC
        </button>
      ) : (
        <>
          <RecipientChips label="CC" value={cc} onChange={setCc} testId="composer-cc" />
          <RecipientChips label="BCC" value={bcc} onChange={setBcc} testId="composer-bcc" />
        </>
      )}

      <div>
        <Label htmlFor="composer-subject" className="text-xs uppercase tracking-wider text-gray-600">Asunto *</Label>
        <Input
          ref={subjectRef}
          id="composer-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onFocus={() => { lastFocusedField.current = "subject"; }}
          placeholder="Asunto del correo"
          data-testid="composer-subject"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs uppercase tracking-wider text-gray-600">Mensaje *</Label>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setTemplatePickerOpen(true)}
              className="text-gray-700 hover:bg-gray-100"
              data-testid="composer-load-template-btn"
            >
              <LayoutTemplate className="w-4 h-4 mr-1.5" /> Cargar plantilla
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setAiOpen(true)}
              className="text-brand-burgundy hover:bg-brand-cream"
              data-testid="composer-ai-btn"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Redactar con IA
            </Button>
          </div>
        </div>

        {/* Iter18 — Quick Reply Buttons (configurables en admin/settings.quick_reply_template_ids) */}
        {quickReplies.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="composer-quick-replies">
            <span className="text-[10px] uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
              <Zap className="w-3 h-3 text-brand-burgundy" /> Respuestas rápidas:
            </span>
            {quickReplies.map((qr) => (
              <button
                key={qr.id}
                type="button"
                onClick={() => applyTemplate(qr)}
                className="text-[11px] bg-brand-cream-soft hover:bg-brand-cream px-2.5 py-1 rounded-sm border border-brand-burgundy/30 hover:border-brand-burgundy text-brand-burgundy hover:text-brand-burgundy-light transition-colors"
                title={qr.description || qr.subject}
                data-testid={`composer-quick-reply-${qr.slug || qr.id}`}
              >
                {qr.name}
              </button>
            ))}
          </div>
        )}

        <div
          className="quill-wrapper bg-white rounded-sm border border-gray-300"
          onFocus={() => { lastFocusedField.current = "body"; }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={html}
            onChange={setHtml}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Escribe el mensaje..."
            style={{ minHeight: 240 }}
          />
        </div>
      </div>

      {/* Placeholders disponibles — siempre visibles (no colapsable) */}
      {placeholders.length > 0 && (
        <div className="border border-gray-200 rounded-sm bg-gray-50/50 p-3">
          <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
            <Tag className="w-3 h-3" />
            <span className="font-semibold">Variables — click para insertar en {lastFocusedField.current === "subject" ? "Asunto" : "Mensaje"}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {placeholders.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => insertPlaceholder(p.key)}
                className="text-[11px] bg-white hover:bg-brand-cream px-2 py-1 rounded-sm border border-gray-300 hover:border-brand-burgundy hover:text-brand-burgundy transition-colors"
                title={p.description}
                data-testid={`placeholder-${p.key.replace(/[{}]/g, "")}`}
              >
                <code className="text-[11px]">{p.key}</code>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mt-2">
            💡 Las variables se reemplazan con valores reales al enviar. Pasa el ratón sobre cada una para ver su descripción.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between pt-3 border-t border-gray-200">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          {savingDraft && <><Loader2 className="w-3 h-3 animate-spin" /> Guardando borrador...</>}
          {!savingDraft && draftSavedAt && <>💾 Borrador guardado {draftSavedAt.toLocaleTimeString("es-CO")}</>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {onClose && (
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          )}
          <Button type="button" variant="outline" onClick={saveDraftNow} disabled={savingDraft} data-testid="composer-save-draft">
            <Save className="w-4 h-4 mr-2" /> Guardar borrador
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setLivePreview((v) => !v)}
            data-testid="composer-toggle-live-preview"
            title={livePreview ? "Ocultar vista previa en vivo" : "Mostrar vista previa en vivo"}
          >
            {livePreview
              ? (<><EyeOff className="w-4 h-4 mr-2" /> Ocultar preview</>)
              : (<><Eye className="w-4 h-4 mr-2" /> Preview en vivo</>)}
          </Button>
          <Button
            type="button"
            onClick={send}
            disabled={sending || to.length === 0 || !subject.trim() || !html.trim()}
            className="bg-brand-burgundy hover:bg-brand-burgundy-light text-white"
            data-testid="composer-send"
          >
            <Send className="w-4 h-4 mr-2" /> {sending ? "Enviando..." : "Enviar"}
          </Button>
        </div>
      </div>
    </>
  );

  // Iter18 — Panel de preview live (usa componente compartido + headerSlot con Para/Asunto)
  const previewHeaderSlot = (
    <>
      <p className="text-[10px] uppercase tracking-widest text-gray-500">Para</p>
      <p className="text-xs text-gray-800 truncate" data-testid="composer-preview-to">{to.join(", ") || "—"}</p>
      {(cc.length > 0 || bcc.length > 0) && (
        <p className="text-[10px] text-gray-500 mt-1 truncate">
          {cc.length > 0 && <>CC: <span className="text-gray-700">{cc.join(", ")}</span></>}
          {cc.length > 0 && bcc.length > 0 && " · "}
          {bcc.length > 0 && <>BCC: <span className="text-gray-700">{bcc.join(", ")}</span></>}
        </p>
      )}
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-2">Asunto</p>
      <p className="text-sm font-medium text-gray-900" data-testid="composer-preview-subject">{subject || "(sin asunto)"}</p>
    </>
  );

  const previewBlock = (
    <div className="bg-gray-100 border border-gray-200 rounded-sm p-4 sticky top-2" data-testid="composer-live-preview">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Vista previa en vivo</p>
      <DevicePreviewIframe
        srcDoc={previewFull}
        testIdPrefix="composer-preview"
        height={560}
        headerSlot={previewHeaderSlot}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {livePreview ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4 min-w-0">{editorBlock}</div>
          <div className="min-w-0">{previewBlock}</div>
        </div>
      ) : (
        <div className="space-y-4">{editorBlock}</div>
      )}

      {/* AI Modal */}
      <AIComposeModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        replyToSubject={replyTo?.subject}
        onGenerated={(g) => {
          if (g.subject && !subject) setSubject(g.subject);
          else if (g.subject && subject) {
            // Si ya hay subject, preguntar si reemplazar (silenciosamente lo ponemos)
            setSubject(g.subject);
          }
          setHtml(g.html);
        }}
      />

      {/* Iter13 — Template Picker Modal */}
      <TemplatePickerModal
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onSelect={(tpl) => {
          applyTemplate(tpl);
          setTemplatePickerOpen(false);
        }}
      />
    </div>
  );
}


/**
 * TemplatePickerModal — selector inline para el Composer.
 */
function TemplatePickerModal({ open, onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ category: "compose", is_active: "true" });
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/admin/emails/templates?${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando plantillas");
    } finally {
      setLoading(false);
    }
  }, [open, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="composer-template-picker">
        <DialogHeader>
          <DialogTitle>Cargar plantilla</DialogTitle>
          <DialogDescription>
            Selecciona una plantilla activa para precargar el asunto y el cuerpo del email.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre o asunto…"
          className="rounded-sm h-9 mb-2"
          data-testid="composer-template-picker-search"
        />
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {loading && <p className="text-sm text-gray-500 py-4 text-center">Cargando…</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-gray-500 py-4 text-center" data-testid="composer-template-picker-empty">
              No hay plantillas disponibles. Crea una en la pestaña <strong>Templates</strong>.
            </p>
          )}
          {!loading && items.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelect(t)}
              className="w-full text-left border border-gray-200 hover:border-brand-burgundy hover:bg-brand-cream/30 transition-colors p-3 rounded-sm"
              data-testid={`composer-template-pick-${t.id}`}
            >
              <p className="text-sm font-medium text-gray-900">{t.name}</p>
              {t.description && <p className="text-[11px] text-gray-500 line-clamp-1">{t.description}</p>}
              <p className="text-xs text-gray-700 mt-1 truncate"><span className="text-gray-400">Asunto:</span> {t.subject}</p>
              {t.variables.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {t.variables.slice(0, 4).map((v) => (
                    <code key={v} className="text-[10px] bg-gray-100 px-1.5 py-[1px] rounded-sm">{`{{${v}}}`}</code>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="composer-template-picker-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
